import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type {
  Artifact,
  ArtifactMessage,
  CollegeShortlistDelta,
  CollegeShortlistPayload,
  MarkdownDelta,
  StudyPlanDelta,
  StudyPlanPayload,
} from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import { BoxType } from '@backrs_ui/grow-ui/src/components/PageShell/resourceTabs/InfoBox.config';
import {
  MainResponse,
  ResourcesContent,
  ResourcesContentItem,
  CollectedData,
  LubavSessionType,
  ResourceKey,
} from '@backrs_ui/grow-ui/src/models/models';

import { setIsStreaming } from '../coachLubav/coachLubav';
import { initiateSession } from '../coachLubav/coachLubav.thunk';
import { getSessionState } from '../lubavSessionState/lubavSessionState.utils';
import { handlePending, handleRejected } from '../reducers';
import { logout } from '../session/logoutThunk';
import { RootState } from '../store';

type BoxData = Readonly<{
  mainResponse?: MainResponse;
  resources?: ResourcesContent;
  collectedData?: CollectedData;
}>;

export type HighlightedNugget = Readonly<{
  resourceKey: ResourceKey;
  id: string;
  tab: BoxType;
}>;

type BoxDataState = Readonly<{
  boxData: BoxData;
  loading: boolean;
  streamingComplete: boolean;
  toolTriggered: boolean;
  currentResponseId?: number;
  highlightedNugget: HighlightedNugget | null;
  artifact?: Artifact;
  artifactStreaming: boolean;
  error?: string;
  unreadTabs: readonly BoxType[];
  lastSeenFingerprints: Partial<Record<BoxType, string>>;
}>;

type ContentItemWithTab = ResourcesContentItem & { tab?: BoxType };

const getTabFingerprints = (
  resources: ResourcesContent | undefined,
): Partial<Record<BoxType, string>> => {
  if (!resources) return {};

  const idsByTab: Partial<Record<BoxType, string[]>> = {};

  for (const key of Object.keys(resources) as ResourceKey[]) {
    const content = resources[key]?.content?.content;

    if (!content?.length) continue;

    for (const item of content) {
      const typedItem = item as ContentItemWithTab;
      const tab = typedItem.tab;

      if (!tab || !typedItem.id) continue;

      const ids = idsByTab[tab] ?? [];

      ids.push(typedItem.id);
      idsByTab[tab] = ids;
    }
  }

  const fingerprints: Partial<Record<BoxType, string>> = {};

  for (const [tab, ids] of Object.entries(idsByTab)) {
    fingerprints[tab as BoxType] = ids.sort().join(',');
  }

  return fingerprints;
};

export const initialState: BoxDataState = {
  boxData: {
    mainResponse: undefined,
    resources: undefined,
    collectedData: undefined,
  },
  loading: false,
  streamingComplete: false,
  toolTriggered: false,
  currentResponseId: undefined,
  highlightedNugget: null,
  artifact: undefined,
  artifactStreaming: false,
  error: undefined,
  unreadTabs: [],
  lastSeenFingerprints: {},
};

const mergeMarkdown = (current: Artifact | undefined, delta: MarkdownDelta): Artifact => {
  const prior = current?.kind === 'markdown' ? current.payload : '';

  return { kind: 'markdown', payload: prior + delta.append };
};

const mergeCollegeShortlist = (
  current: Artifact | undefined,
  delta: CollegeShortlistDelta,
): Artifact => {
  const prior: CollegeShortlistPayload =
    current?.kind === 'college_shortlist' ? current.payload : { safe: [], target: [], stretch: [] };
  const next: CollegeShortlistPayload = {
    safe: [...(prior.safe ?? [])],
    target: [...(prior.target ?? [])],
    stretch: [...(prior.stretch ?? [])],
  };
  const arr = next[delta.bucket] ?? [];

  while (arr.length <= delta.index) arr.push({});
  arr[delta.index] = delta.school;
  next[delta.bucket] = arr;

  return { kind: 'college_shortlist', payload: next };
};

const mergeStudyPlan = (current: Artifact | undefined, delta: StudyPlanDelta): Artifact => {
  const prior: StudyPlanPayload =
    current?.kind === 'study_plan' ? current.payload : { priorities: [], weeks: [] };
  const next: StudyPlanPayload = {
    priorities: [...prior.priorities],
    weeks: [...prior.weeks],
  };

  if (delta.field === 'priorities') {
    next.priorities = [...delta.value];
  } else {
    const idx = delta.index - 1;

    while (next.weeks.length <= idx) {
      next.weeks.push({
        week: next.weeks.length + 1,
        goals: [],
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
      });
    }
    next.weeks[idx] = delta.value;
  }

  return { kind: 'study_plan', payload: next };
};

const applyArtifactMessage = (
  current: Artifact | undefined,
  message: ArtifactMessage,
): Artifact | undefined => {
  switch (message.kind) {
    case 'markdown':
      return mergeMarkdown(current, message.delta);
    case 'college_shortlist':
      return mergeCollegeShortlist(current, message.delta);
    case 'study_plan':
      return mergeStudyPlan(current, message.delta);
    default: {
      const _exhaustive: never = message;

      console.warn('Unknown artifact kind, preserving current artifact', _exhaustive);

      return current;
    }
  }
};

export const boxDataSlice = createSlice({
  name: 'boxData',
  initialState,
  reducers: {
    setIsLoadingResources: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setStreamingComplete: (state, action: PayloadAction<boolean>) => {
      state.streamingComplete = action.payload;
    },
    setBoxData: (state, action: PayloadAction<BoxData>) => {
      state.boxData = action.payload;
    },
    setMainResponseData: (state, action: PayloadAction<MainResponse>) => {
      state.boxData.mainResponse = action.payload;
    },
    setResourcesContent: (state, action: PayloadAction<ResourcesContent | undefined>) => {
      if (action.payload) {
        state.boxData.resources = action.payload;
      }
    },
    setCollectedData: (state, action: PayloadAction<CollectedData>) => {
      state.boxData.collectedData = action.payload;
    },
    setCurrentResponseId: (state, action: PayloadAction<number>) => {
      state.currentResponseId = action.payload;
    },
    setToolTriggered: (state, action: PayloadAction<boolean>) => {
      state.toolTriggered = action.payload;
    },
    setHighlightedNugget: (state, action: PayloadAction<HighlightedNugget | null>) => {
      state.highlightedNugget = action.payload;
    },
    setArtifact: (state, action: PayloadAction<Artifact | undefined>) => {
      state.artifact = action.payload;
    },
    setArtifactStreaming: (state, action: PayloadAction<boolean>) => {
      state.artifactStreaming = action.payload;
    },
    applyArtifactDelta: (state, action: PayloadAction<ArtifactMessage>) => {
      state.artifact = applyArtifactMessage(state.artifact, action.payload);
    },
    resetBoxData: (state) => {
      state.boxData = initialState.boxData;
      state.loading = false;
      state.streamingComplete = false;
      state.toolTriggered = false;
      state.highlightedNugget = null;
      state.artifact = undefined;
      state.artifactStreaming = false;
      state.error = undefined;
      state.unreadTabs = [];

      state.lastSeenFingerprints = {};
    },
    removeResourcesByKeys: (state, action: PayloadAction<ResourceKey[]>) => {
      if (!state.boxData.resources) return;

      const updated = { ...state.boxData.resources };

      for (const key of action.payload) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete updated[key];
      }

      state.boxData.resources = updated;
    },
    removeNonFeaturedResources: (state, action: PayloadAction<ResourceKey[]>) => {
      const resources = state.boxData.resources;

      if (!resources) return;

      const updated: Record<string, unknown> = {};

      for (const key of Object.keys(resources) as ResourceKey[]) {
        const data = resources[key];
        const items = action.payload.includes(key)
          ? data?.content?.content?.filter(
              (item) => 'isFeatured' in item && item.isFeatured === true,
            )
          : data?.content?.content;

        if (items?.length) {
          updated[key] = { ...data, content: { content: items } };
        }
      }

      state.boxData.resources = updated as ResourcesContent;
    },
    restoreResources: (state, action: PayloadAction<Partial<ResourcesContent>>) => {
      state.boxData.resources = {
        ...(state.boxData.resources ?? {}),
        ...action.payload,
      };
    },
    markTabRead: (state, action: PayloadAction<BoxType>) => {
      state.unreadTabs = state.unreadTabs.filter((t) => t !== action.payload);

      // Snapshot fingerprint for this tab so future changes are detected correctly
      if (state.boxData.resources) {
        const fingerprints = getTabFingerprints(state.boxData.resources);

        if (fingerprints[action.payload]) {
          state.lastSeenFingerprints[action.payload] = fingerprints[action.payload];
        }
      }
    },
    resetMainResponse: (state) => {
      state.boxData.mainResponse = initialState.boxData.mainResponse;
    },
    resetSolutionSessionBoxData: (state) => {
      state.boxData = initialState.boxData;
      state.loading = false;
      state.streamingComplete = false;
      state.toolTriggered = false;
      state.currentResponseId = undefined;
      state.highlightedNugget = null;
      state.artifact = undefined;
      state.artifactStreaming = false;
      state.error = undefined;
      state.unreadTabs = [];

      state.lastSeenFingerprints = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initiateSession.pending, handlePending)
      .addCase(initiateSession.rejected, handleRejected)
      .addCase(initiateSession.fulfilled, (state) => {
        state.boxData = initialState.boxData;
        state.loading = false;
        state.streamingComplete = false;
        state.toolTriggered = false;
        state.highlightedNugget = null;
        state.artifact = undefined;
        state.artifactStreaming = false;
        state.error = undefined;
        state.unreadTabs = [];

        state.lastSeenFingerprints = {};
      })

      // When AI streaming ends, detect which tabs have changed content
      .addCase(setIsStreaming, (state, action) => {
        if (action.payload || !state.boxData.resources) return;

        const fingerprints = getTabFingerprints(state.boxData.resources);

        for (const [tab, fingerprint] of Object.entries(fingerprints) as [BoxType, string][]) {
          const lastSeen = state.lastSeenFingerprints[tab] ?? '';

          state.lastSeenFingerprints[tab] = fingerprint;

          if (fingerprint !== '' && fingerprint !== lastSeen) {
            if (!state.unreadTabs.includes(tab)) {
              (state.unreadTabs as BoxType[]).push(tab);
            }
          }
        }
      })

      // LOGOUT
      .addCase(logout, (state) => {
        state.boxData = initialState.boxData;
        state.loading = false;
        state.streamingComplete = false;
        state.toolTriggered = false;
        state.highlightedNugget = null;
        state.artifact = undefined;
        state.artifactStreaming = false;
        state.error = undefined;
        state.unreadTabs = [];

        state.lastSeenFingerprints = {};
      });
  },
});

export const {
  setIsLoadingResources,
  setStreamingComplete,
  setBoxData,
  setMainResponseData,
  setResourcesContent,
  setCollectedData,
  setCurrentResponseId,
  setToolTriggered,
  setHighlightedNugget,
  setArtifact,
  setArtifactStreaming,
  applyArtifactDelta,
  resetBoxData,
  resetMainResponse,
  resetSolutionSessionBoxData,
  markTabRead,
  removeResourcesByKeys,
  removeNonFeaturedResources,
  restoreResources,
} = boxDataSlice.actions;

export const boxDataReducer = boxDataSlice.reducer;

export const selectResourcesContent = (state: RootState, sessionType: LubavSessionType | null) =>
  getSessionState(state, sessionType)?.boxData?.boxData?.resources;

export const selectHighlightedNugget = (state: RootState, sessionType: LubavSessionType | null) =>
  getSessionState(state, sessionType)?.boxData?.highlightedNugget ?? null;

export const selectArtifact = (state: RootState, sessionType: LubavSessionType | null) =>
  getSessionState(state, sessionType)?.boxData?.artifact;

export const selectArtifactStreaming = (state: RootState, sessionType: LubavSessionType | null) =>
  getSessionState(state, sessionType)?.boxData?.artifactStreaming ?? false;

export const selectUnreadTabs = (state: RootState, sessionType: LubavSessionType | null) =>
  getSessionState(state, sessionType)?.boxData?.unreadTabs ?? [];
